/**
 * MobileRecipes — Recipes tab list.
 *
 * Layout matches the mobile mockup:
 *   1. 4 category tiles (Products · Stores · Deals · Charities)
 *   2. Big purple "+ Post New Recipes" pill that jumps to
 *      /m/add-recipe (auth-gated inside that screen).
 *   3. Search Recipe input + Search button.
 *   4. Vertical list of Recipe cards — title + category + hero image
 *      + green "View Details" CTA.
 *
 * Data comes from ``GET /api/recipes``.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { CATEGORY_TILES, CategoryTile } from "./MobileHome";

const API = process.env.REACT_APP_BACKEND_URL;
const FP_PURPLE = "#653091";
const FP_GREEN = "#8AC136";

export default function MobileRecipes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const runSearch = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/recipes`, {
        params: { q: q || undefined, limit: 30, sort: "new" },
      });
      // API returns { items } but also legacy { recipes } — handle both.
      setItems(res.data.items || res.data.recipes || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { runSearch(); /* eslint-disable-line */ }, []);

  return (
    <div className="pb-10" data-testid="mobile-recipes-page">
      {/* Category tiles */}
      <div
        className="grid grid-cols-4 gap-2 px-3 pt-3 pb-4"
        style={{ background: "linear-gradient(180deg,#B5DA5E 0%,#CBEB90 100%)" }}
      >
        {CATEGORY_TILES.map((t) => (
          <CategoryTile key={t.key} tile={t} />
        ))}
      </div>

      {/* Post new recipes CTA */}
      <div className="px-4 mt-3">
        <Link
          to="/m/add-recipe"
          data-testid="post-new-recipe-btn"
          className="w-full flex items-center justify-center rounded-2xl py-4 text-white text-lg font-bold shadow"
          style={{ background: FP_PURPLE }}
        >
          + Post New Recipes
        </Link>
      </div>

      {/* Search */}
      <div className="px-4 mt-3 flex items-center gap-2">
        <div className="flex-1 flex items-center gap-2 bg-white rounded-xl border border-gray-200 px-3 py-3">
          <input
            data-testid="recipes-search-input"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSearch()}
            placeholder="Search Recipe"
            className="flex-1 outline-none text-base bg-transparent"
          />
          <Search className="w-4 h-4 opacity-60" />
        </div>
        <button
          data-testid="recipes-search-btn"
          onClick={runSearch}
          className="px-6 py-3 rounded-xl text-white text-base font-semibold"
          style={{ background: FP_PURPLE }}
        >
          Search
        </button>
      </div>

      {/* Results */}
      <div className="px-4 mt-4 space-y-4" data-testid="recipes-list">
        {loading ? (
          <div className="text-sm opacity-60">Loading…</div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center text-sm opacity-60">
            No recipes yet — be the first to Post New Recipes!
          </div>
        ) : (
          items.map((r) => {
            const slug = r.slug || r.id;
            const cover = r.cover_photo || r.hero_image || r.gallery?.[0]?.data;
            return (
              <div
                key={slug}
                data-testid={`recipe-card-${slug}`}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
              >
                <div className="flex items-stretch gap-3 p-3">
                  <div className="flex-1 min-w-0 flex flex-col justify-between">
                    <div>
                      <h3 className="text-lg font-bold leading-tight">{r.title}</h3>
                      <div className="text-sm text-gray-600 mt-1 capitalize">{r.category || r.type}</div>
                    </div>
                    <Link
                      to={`/m/recipes/${slug}`}
                      data-testid={`recipe-view-${slug}`}
                      className="mt-3 self-start px-4 py-2 rounded-lg text-white text-sm font-semibold"
                      style={{ background: FP_GREEN }}
                    >
                      View Details
                    </Link>
                  </div>
                  {cover && (
                    <img
                      src={cover}
                      alt={r.title}
                      loading="lazy"
                      className="w-32 h-32 rounded-xl object-cover"
                    />
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
